/**
 * `session.me` → `AuthState` mapping: the pure projection + equality helpers
 * the BFF session adapter (`RealSessionProvider`) consumes, extracted into their
 * own module (R3: small focused units, and to keep `RealSessionProvider.tsx`
 * under the 400-line architecture ceiling). No instance state, no React, no I/O
 * — just the type seam and two pure functions, so they are trivially unit
 * testable and reusable by the colocated provider test.
 *
 * Owns the dual-audience-collapse shape work (032, RD-111): the member arm of
 * the `session.me` projection and the member-aware structural equality.
 */

import type { WebTrpcClient } from '../trpc/client.js';
import type { AuthState } from './SessionPort.js';

/**
 * The `session.me` result shape, DERIVED from the `AppRouter`-typed client
 * rather than imported as a named type. Web code's single source-level coupling
 * to the API is the `AppRouter` type behind `WebTrpcClient`
 * (`@omaplus/shared-types/trpc`); reaching into the API module that declares
 * `SessionMeResult` would bypass that named seam. `Awaited<ReturnType<…me.query>>`
 * reads the exact contract off the client type, so a server-side change to the
 * union widens this alias automatically with no edit here. Exported so the
 * provider and its colocated test narrow on the same derived type.
 *
 * The API's `session.me` member arm has landed (032 US1 backend track), so the
 * derived union now carries `{anonymous} | {admin} | {member, …}` and
 * `mapSessionMeToAuthState` narrows its member branch off THIS derived type
 * directly — no local forward-declaration of the member shape is needed any more
 * (the earlier `MemberSessionMe` / `SessionMeOrMember` widening retired once the
 * arm shipped). The single load-bearing coupling to the API stays the `AppRouter`
 * type behind `WebTrpcClient`.
 */
export type SessionMeResult = Awaited<ReturnType<WebTrpcClient['session']['me']['query']>>;

/**
 * Map the BFF `session.me` result to the consumer-facing `AuthState`. Serves
 * BOTH audiences from one round-trip (RD-111):
 *
 *   - `admin`  -> the admin arm, `demo: false`.
 *   - `member` -> the member arm carrying the claim-derived profile, the
 *     server-snapshotted `membershipStatus`, the jäsennumero (`memberNumber`),
 *     `demo: false` (this is the REAL adapter — the `demo` discriminator is
 *     `true` only under `DemoSessionProvider`), and `onboardingCompleted: true`
 *     (a real member onboards server-side at sign-in; there is no client wizard,
 *     RD-105). No `demoMemberKey` (that is the demo adapter's per-fixture
 *     discriminator), so `useMembershipStatus` reads the trusted
 *     `membershipStatus` directly.
 *   - anything else (`anonymous`) -> the bare anonymous arm.
 *
 * Accepts the DERIVED `SessionMeResult` directly: the API's `session.me` member
 * arm has landed, so the derived union narrows to `member` on the runtime `kind`
 * discriminator with no local widening. Pure: reads no instance state, so it is
 * trivially testable.
 */
export function mapSessionMeToAuthState(result: SessionMeResult): AuthState {
  if (result.kind === 'admin') {
    return {
      kind: 'admin',
      email: result.email,
      displayName: result.displayName,
      demo: false,
    };
  }
  if (result.kind === 'member') {
    return {
      kind: 'member',
      email: result.email,
      displayName: result.displayName,
      // Binary access (042 / D-202): the client status is `'none' | 'valid'`.
      // The BFF snapshot type is still `'valid' | 'lapsed'` (the auth callback
      // now mints only `'valid'`, but the DB column keeps the wider historical
      // shape). Map at the boundary fail-closed — only `'valid'` stays `'valid'`;
      // anything else (a stale `'lapsed'` session minted before this change)
      // becomes `'none'`, so an ended membership loses member access exactly as
      // D-202 requires, without a client `lapsed` state.
      membershipStatus: result.membershipStatus === 'valid' ? 'valid' : 'none',
      memberNumber: result.memberNumber,
      // Omitted, not `undefined`, when the member has never chosen (059).
      ...(result.language === null ? {} : { language: result.language }),
      demo: false,
      onboardingCompleted: true,
    };
  }
  return { kind: 'anonymous' };
}

/**
 * Structural equality for every `AuthState` arm the BFF provider moves through
 * (`loading` initial seed, then `anonymous`, `admin`, or `member`). Used by the
 * provider's `#transitionTo` to suppress no-op notifications so a refresh that
 * resolves the same identity does not fire a spurious re-render.
 *
 * The `member` comparison is the dual-audience-collapse addition (RD-111): with
 * the member arm now produced by the map, a member->member refresh MUST compare
 * the member fields, otherwise every such refresh would return false and fire a
 * wasteful (though harmless — `useSyncExternalStore` re-reads the unchanged
 * state) notify. The compared fields are exactly those the real adapter sets
 * (`email`, `displayName`, `membershipStatus`, `memberNumber`, `language`,
 * `demo`, `onboardingCompleted`); `demoMemberKey` is never set on the real arm, so it is
 * not compared.
 */
export function authStatesEqual(a: AuthState, b: AuthState): boolean {
  if (a.kind !== b.kind) {
    return false;
  }
  if (a.kind === 'admin' && b.kind === 'admin') {
    return a.email === b.email && a.displayName === b.displayName && a.demo === b.demo;
  }
  if (a.kind === 'member' && b.kind === 'member') {
    return (
      a.email === b.email &&
      a.displayName === b.displayName &&
      a.membershipStatus === b.membershipStatus &&
      a.memberNumber === b.memberNumber &&
      a.language === b.language &&
      a.demo === b.demo &&
      a.onboardingCompleted === b.onboardingCompleted
    );
  }
  // Same kind, neither admin nor member: `loading` (the seed) and `anonymous`
  // are both bare-discriminant arms with no payload, so a matched `kind` is
  // exact equality.
  return (
    (a.kind === 'anonymous' && b.kind === 'anonymous') ||
    (a.kind === 'loading' && b.kind === 'loading') ||
    (a.kind === 'signingOut' && b.kind === 'signingOut') ||
    (a.kind === 'signOutFailed' && b.kind === 'signOutFailed')
  );
}
